// Define actions, computed values and all necesary to manage the state client side
import { Store, SerializedData } from "bernie-core";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { observable, computed, action } from "mobx";
import {
  PackageWizardState,
  GlobalWizardState,
  LxWizardState,
  VrWizardState,
  CarWizardState,
  HotelWizardState,
  ThirdPartyPackageWizardState,
  CruiseWizardState,
  FlightWizardState,
  ExternalLinkTabWizardState,
  LOBState,
} from "stores/wizard/state";
import { MultiLOBWizardConfig } from "./config";
import { LastSearchData } from "stores/wizard/LastSearchData";
import { WizardStore } from "./WizardStore";
import { LOB_KEYS } from "components/shared/StorefrontWizard/constants";

export class ClientWizardStateStore extends Store implements WizardStore {
  @observable
  public multiLOBWizardConfig: MultiLOBWizardConfig;

  @observable
  public lastSearchData?: LastSearchData;

  @observable
  public lobStateMap: any;

  @observable
  public canTrack: boolean;

  @computed
  public get defaultLob() {
    return this.globalWizardState?.defaultLob;
  }

  @computed
  public get useDefaultLOB() {
    return this.globalWizardState.useDefaultLOB;
  }

  public setUseDefaultLOB(useDefaultLOB: boolean) {
    this.globalWizardState.useDefaultLOB = useDefaultLOB;
  }

  @computed
  public get activeLob() {
    return this.globalWizardState?.activeLob;
  }

  @action
  public setActiveLob(activeLob: string) {
    this.globalWizardState.setActiveLob(activeLob);
    if (this.lobStateMap[activeLob]) {
      (this.lobStateMap[activeLob] as LOBState).resetValidations();
    }
    if (activeLob === LOB_KEYS.GT) {
      this.lobStateMap[LOB_KEYS.CAR].groundTransportationLocationState.resetValidations();
    }
  }

  @observable
  public globalWizardState: GlobalWizardState;

  @observable
  public packageWizardState: PackageWizardState;

  @observable
  public lxWizardState: LxWizardState;

  @observable
  public externalLinkTabWizardState: ExternalLinkTabWizardState;

  @observable
  public vrWizardState: VrWizardState;

  @observable
  public carWizardState: CarWizardState;

  @observable
  public hotelWizardState: HotelWizardState;

  @observable
  public flightWizardState: FlightWizardState;

  @observable
  public thirdPartyPackageWizardState: ThirdPartyPackageWizardState;

  @observable
  public cruiseWizardState: CruiseWizardState;

  constructor(state: SerializedData = {}, logger: Logger = NOOP_LOGGER) {
    super(state, logger);
  }

  public hydrate(data: SerializedData): void {
    if (!data.multiLOBWizardConfig) {
      return;
    }

    this.multiLOBWizardConfig = data.multiLOBWizardConfig as MultiLOBWizardConfig;
    this.lastSearchData = data.lastSearchData as LastSearchData;
    this.canTrack = data.canTrack as boolean;

    this.globalWizardState = new GlobalWizardState(
      this.multiLOBWizardConfig.global,
      this.canTrack,
      this.lastSearchData
    );

    this.packageWizardState = new PackageWizardState(
      this.multiLOBWizardConfig.package,
      this.globalWizardState,
      this.lastSearchData
    );

    this.lxWizardState = new LxWizardState(this.multiLOBWizardConfig.lx, this.globalWizardState, this.lastSearchData);

    this.vrWizardState = new VrWizardState(this.multiLOBWizardConfig.vr, this.globalWizardState, this.lastSearchData);

    this.carWizardState = new CarWizardState(
      this.multiLOBWizardConfig.car,
      this.globalWizardState,
      this.lastSearchData
    );

    this.hotelWizardState = new HotelWizardState(
      this.multiLOBWizardConfig.hotel,
      this.globalWizardState,
      this.logger,
      this.lastSearchData
    );

    this.flightWizardState = new FlightWizardState(
      this.multiLOBWizardConfig.flight,
      this.globalWizardState,
      this.lastSearchData
    );

    this.thirdPartyPackageWizardState = new ThirdPartyPackageWizardState(
      this.multiLOBWizardConfig.thirdPartyPackage,
      this.globalWizardState,
      this.lastSearchData
    );

    this.cruiseWizardState = new CruiseWizardState(this.multiLOBWizardConfig.cruise, this.globalWizardState);

    this.externalLinkTabWizardState = new ExternalLinkTabWizardState(this.multiLOBWizardConfig.externalLinkTab);

    this.lobStateMap = {
      "wizard-flight-pwa": this.flightWizardState,
      "wizard-car-pwa": this.carWizardState,
      "wizard-vr-pwa": this.vrWizardState,
      "wizard-package-pwa": this.packageWizardState,
      "wizard-lx-pwa": this.lxWizardState,
      "wizard-thirdPartyPackage-pwa": this.thirdPartyPackageWizardState,
      "wizard-cruise-pwa": this.cruiseWizardState,
      "wizard-hotel-pwa": this.hotelWizardState,
      "wizard-hotel-pwa-v2": this.hotelWizardState,
      "wizard-externalLinkTab-pwa": this.externalLinkTabWizardState,
    };
  }
}
